import type { LocalRecoveryDraft } from '@safewallet/core';

const DB_NAME = 'safe-wallet-recovery';
const DB_VERSION = 3;
const STORE = 'state';
const LEGACY_CUSTODIAN_IDENTITY_KEY = 'custodian-identity-v3';
const PENDING_RECOVERY_KEY = 'pending-recovery-v2';

export interface StoredRecoveryDraft {
  version: 2;
  recoverySetId: string;
  encryptedDraft: LocalRecoveryDraft;
  createdAt: string;
}

export interface StoredPendingRecovery {
  version: 2;
  requestId: string;
  recoverySetId: string;
  vaultId: string;
  recoveringPublicKey: string;
  recoveringPrivateKey: CryptoKey;
  fingerprint: string;
  ownerEmail: string;
  createdAt: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      const store = database.objectStoreNames.contains(STORE)
        ? request.transaction?.objectStore(STORE)
        : database.createObjectStore(STORE);
      store?.delete(LEGACY_CUSTODIAN_IDENTITY_KEY);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open recovery storage.'));
  });
}

async function getValue<T>(key: string): Promise<T | null> {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = database.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      request.onsuccess = () => resolve((request.result as T | undefined) ?? null);
      request.onerror = () => reject(request.error ?? new Error('Could not read recovery storage.'));
    });
  } finally {
    database.close();
  }
}

async function setValue<T>(key: string, value: T): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).put(value, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Could not save recovery storage.'));
      transaction.onabort = () => reject(transaction.error ?? new Error('Recovery storage write was cancelled.'));
    });
  } finally {
    database.close();
  }
}

async function deleteValue(key: string): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE, 'readwrite');
      transaction.objectStore(STORE).delete(key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('Could not clear recovery storage.'));
    });
  } finally {
    database.close();
  }
}

export const loadRecoveryDraft = (recoverySetId: string) => getValue<StoredRecoveryDraft>(`draft:${recoverySetId}`);
export const saveRecoveryDraft = (draft: StoredRecoveryDraft) => setValue(`draft:${draft.recoverySetId}`, draft);
export const clearRecoveryDraft = (recoverySetId: string) => deleteValue(`draft:${recoverySetId}`);

export const loadPendingRecovery = () => getValue<StoredPendingRecovery>(PENDING_RECOVERY_KEY);
export const savePendingRecovery = (pending: StoredPendingRecovery) => setValue(PENDING_RECOVERY_KEY, pending);
export const clearPendingRecovery = () => deleteValue(PENDING_RECOVERY_KEY);
